import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Badge, Form, Modal, Button, Row, Col } from 'react-bootstrap';
import { FaListUl, FaEdit } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { getOrdersApi, updateOrderStatusApi } from '../menu/service/api_service';
import { API_BASE_URL } from '../../config/api';
import SharedOrderDetailModal from './SharedOrderDetailModal';

export default function OrderHistory() {
  const token = localStorage.getItem('token');
  const [orders, setOrders] = useState([]);
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  const fetchOrders = async () => {
    try {
      // Fetch all orders, no status filter
      const res = await getOrdersApi(token);
      const data = res.data;
      if (Array.isArray(data)) {
        setOrders(data);
      } else {
        console.error('Invalid data format:', data);
        setOrders([]);
      }
    } catch (err) {
      console.error('Fetch error:', err);
    }
  };

  useEffect(() => {
    fetchOrders();
    
    const eventSource = new EventSource(`${API_BASE_URL}/api/orders/stream?token=${token}`);
    
    eventSource.addEventListener('ORDER_CREATED', (e) => {
      const newOrder = JSON.parse(e.data);
      setOrders(prev => {
        if (!prev.find(o => o.id === newOrder.id)) {
          return [newOrder, ...prev];
        }
        return prev;
      });
    });

    eventSource.addEventListener('ORDER_UPDATED', (e) => {
      const updatedOrder = JSON.parse(e.data);
      setOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
      setSelectedOrder(prev => prev && prev.id === updatedOrder.id ? updatedOrder : prev);
    });

    return () => {
      eventSource.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const filteredOrders = orders.filter(o => {
    if (!filterDate) return true;
    const orderDate = new Date(o.created_at).toISOString().split('T')[0];
    return orderDate === filterDate;
  });

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending': return <Badge bg="secondary">Pending</Badge>;
      case 'cooking': return <Badge bg="warning" text="dark">Cooking</Badge>;
      case 'ready': return <Badge bg="primary">Ready to Deliver</Badge>;
      case 'completed': return <Badge bg="success">Completed</Badge>;
      case 'cancelled': return <Badge bg="danger">Cancelled</Badge>;
      default: return <Badge bg="dark">{status}</Badge>;
    }
  };

  const handleRowClick = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  const setShortcutDate = (offsetDays) => {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    setFilterDate(d.toISOString().split('T')[0]);
  };

  const handleSaveOrder = async (updatedOrderData, slipFile) => {
    try {
      const confirmResult = await Swal.fire({
        title: 'Save Changes',
        text: `Are you sure you want to save changes to order #${selectedOrder.id}?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, save it!'
      });

      if (confirmResult.isConfirmed) {
        const payload = { 
          status: updatedOrderData.status,
          payment_status: updatedOrderData.payment_status,
          slip_image_url: updatedOrderData.slip_image_url,
          recipient_name: updatedOrderData.recipient_name,
          delivery_time: updatedOrderData.delivery_time
        };

        const formData = new FormData();
        formData.append("orderData", JSON.stringify(payload));
        if (slipFile) {
          formData.append("slip", slipFile);
        }

        await updateOrderStatusApi(token, selectedOrder.id, formData);
        await fetchOrders(); 
        Swal.fire('Saved!', 'Order has been updated successfully.', 'success');
        setShowModal(false);
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to update order", "error");
    }
  };

  return (
    <Container fluid className="px-3 py-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <h4 className="fw-bold m-0"><FaListUl className="me-2 text-primary" /> All Orders</h4>
        
        <div className="d-flex flex-wrap align-items-center gap-2">
          <span className="fw-bold text-muted small d-none d-sm-inline">Filter:</span>
          <Button variant="outline-secondary" size="sm" onClick={() => setShortcutDate(-1)}>Yesterday</Button>
          <Button variant="outline-primary" size="sm" onClick={() => setShortcutDate(0)}>Today</Button>
          <Button variant="outline-secondary" size="sm" onClick={() => setShortcutDate(1)}>Tomorrow</Button>
          <Button variant="outline-danger" size="sm" onClick={() => setFilterDate('')}>All</Button>
          <Form.Control 
            type="date" 
            size="sm"
            value={filterDate} 
            onChange={(e) => setFilterDate(e.target.value)}
            style={{ width: 'auto' }}
          />
          <Badge bg="light" text="dark" className="border py-1 px-3">
            {filteredOrders.length} Orders
          </Badge>
        </div>
      </div>

      <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
        <div className="table-responsive">
          <Table hover className="m-0 align-middle">
            <thead className="bg-light">
              <tr>
                <th className="py-3 px-4 border-0">Order ID</th>
                <th className="py-3 border-0">Time</th>
                <th className="py-3 border-0">Recipient</th>
                <th className="py-3 border-0">Items</th>
                <th className="py-3 border-0">Total Price</th>
                <th className="py-3 px-4 border-0 text-end">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-5 text-muted">
                    No orders found for this date.
                  </td>
                </tr>
              ) : (
                filteredOrders.map(order => {
                  const itemCount = order.items ? order.items.reduce((sum, item) => sum + item.quantity, 0) : 0;
                  return (
                    <tr 
                      key={order.id} 
                      onClick={() => handleRowClick(order)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="px-4 fw-bold text-muted">#{order.id}</td>
                      <td>{new Date(order.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</td>
                      <td className="fw-medium">{order.recipient_name || '-'}</td>
                      <td>{itemCount} {itemCount === 1 ? 'item' : 'items'}</td>
                      <td className="fw-bold text-primary">฿{Number(order.total_price).toLocaleString()}</td>
                      <td className="px-4 text-end">
                        {getStatusBadge(order.status)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
      </Card>

      {/* Order Details Modal */}
      <SharedOrderDetailModal
        show={showModal}
        onHide={() => setShowModal(false)}
        order={selectedOrder}
        readOnly={false}
        onSaveOrder={handleSaveOrder}
      />
    </Container>
  );
}
